import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import Popover from 'tio-ui/components/popover';
import WidgetRenderer from './widget-renderer.gts';
import {
  summarizeFilterConfig,
  type FilterSummaryLine,
} from '../../utils/dashboard-builder/filter-summary.ts';
import type {
  WidgetConfig,
  BuildFilterConfig,
  UserFilterConfig,
} from '../../utils/dashboard-builder/widget-config-types.ts';
import {
  drilldownsFor,
  summarizeDrilldowns,
  type Drilldown,
  type DrilldownMode,
} from '../../utils/dashboard-builder/drilldown.ts';

// WidgetTile – chrome around a single widget: header (title, drag handle,
// remove, filters-on indicator) + the live renderer. Used by the builder grid
// and the read-only view.

interface WidgetTileSignature {
  Args: {
    widget: WidgetConfig;
    selected?: boolean;
    editable?: boolean;
    companyId?: string;
    buildFilters?: BuildFilterConfig;
    userFilters?: UserFilterConfig;
    /**
     * True when the viewer has actually changed the user filters away from the
     * dashboard's saved defaults (view mode only). Author-saved defaults are
     * NOT the viewer's doing, and the builder has no viewer at all, so both
     * leave this false — the tile then reports these as the dashboard's own
     * viewer filters rather than "yours".
     */
    userFiltersModified?: boolean;
    /** Active dashboard drilldowns; the tile lists the ones that reach this widget. */
    drilldowns?: Drilldown[];
    /** A click on a widget's breakdown value; `set` replaces without the toggle-off, `toggle` clears a re-selected value. */
    onDrilldown?: (drilldown: Drilldown, mode: DrilldownMode) => void;
    onSelect?: () => void;
    onRemove?: () => void;
    /** Expand to fill the dashboard canvas (view mode). */
    onExpand?: () => void;
  };
  Element: HTMLDivElement;
}

export default class WidgetTile extends Component<WidgetTileSignature> {
  @tracked showFilterDetails = false;

  // Canvas height fills the tile: subtract header (~37) + padding (24) + legend (~28).
  get chartHeight() {
    if (this.args.widget.format?.height) return this.args.widget.format.height;
    return Math.max(100, this.args.widget.layout.h - 92);
  }

  // ── filters-on indicator ────────────────────────────────────────────────────
  get widgetFilterLines(): FilterSummaryLine[] {
    return summarizeFilterConfig(this.args.widget.buildFilter);
  }

  get dashboardFilterLines(): FilterSummaryLine[] {
    return summarizeFilterConfig(this.args.buildFilters);
  }

  /**
   * Viewer-adjustable filters, listed separately: a dashboard user filter
   * applies to every widget, so what a viewer needs to know is not WHICH
   * widgets moved (all of them) but that this widget's numbers reflect their
   * own selection rather than the saved definition.
   */
  get userFilterLines(): FilterSummaryLine[] {
    return summarizeFilterConfig(this.args.userFilters);
  }

  /** Viewer-adjustable filters exist and carry values (either surface). */
  get hasUserFilters() {
    return this.userFilterLines.length > 0;
  }

  /** Drilldowns from other widgets on this dataset — always the viewer's own doing. */
  get drilldownLines(): FilterSummaryLine[] {
    return summarizeDrilldowns(drilldownsFor(this.args.drilldowns, this.args.widget));
  }

  get hasDrilldowns() {
    return this.drilldownLines.length > 0;
  }

  /** This widget emitted an active drilldown; its border marks it as the source. */
  get isDrilldownSource() {
    return (this.args.drilldowns ?? []).some((d) => d.sourceWidgetId === this.args.widget.id);
  }

  get borderClass() {
    return this.args.selected || this.isDrilldownSource ? 'tio-border-brand' : 'tio-border-default';
  }

  /**
   * Only a viewer's OWN deviation from the saved defaults earns the marked
   * badge — otherwise every tile would light up on first load of a dashboard
   * whose author saved default values, and in the builder too.
   */
  get isViewerAdjusted() {
    return (this.hasUserFilters && Boolean(this.args.userFiltersModified)) || this.hasDrilldowns;
  }

  /** "Your filters" only when they are actually the viewer's. */
  get userFilterHeading() {
    return this.args.userFiltersModified ? 'Your filters' : 'Viewer filters';
  }

  get filterCount() {
    return (
      this.widgetFilterLines.length +
      this.dashboardFilterLines.length +
      this.userFilterLines.length +
      this.drilldownLines.length
    );
  }

  toggleFilterDetails = (event: MouseEvent) => {
    event.stopPropagation();
    this.showFilterDetails = !this.showFilterDetails;
  };

  closeFilterDetails = () => {
    this.showFilterDetails = false;
  };

  handleSelect = () => {
    this.args.onSelect?.();
  };

  handleRemove = (event: MouseEvent) => {
    event.stopPropagation();
    this.args.onRemove?.();
  };

  handleExpand = (event: MouseEvent) => {
    event.stopPropagation();
    this.args.onExpand?.();
  };

  <template>
    <div
      data-test-widget-tile
      data-test-widget-tile-drilldown-source={{if this.isDrilldownSource "true"}}
      class="tio-col h-full overflow-hidden rounded-xl border tio-bg-surface {{this.borderClass}}"
      ...attributes
    >
      <header
        data-drag-handle
        class="tio-row-between gap-2 border-b tio-border-subtle px-3 py-2
          {{if @editable 'cursor-move select-none'}}"
      >
        <button
          type="button"
          data-test-tile-select
          class="truncate text-left tio-text-sm-semibold"
          title={{if @widget.description @widget.description}}
          {{on "click" this.handleSelect}}
        >{{@widget.label}}</button>
        {{#if this.filterCount}}
          {{! Portaled popover — the tile is overflow-hidden, so an absolutely
              positioned dropdown would be clipped inside the widget. }}
          <Popover
            @isOpen={{this.showFilterDetails}}
            @onClose={{this.closeFilterDetails}}
            @maxWidth={{280}}
            @ariaLabel="Filter details"
          >
            <:trigger as |t|>
              <button
                type="button"
                data-test-tile-filters
                data-test-tile-user-filtered={{if this.isViewerAdjusted "true"}}
                title={{if
                  this.isViewerAdjusted
                  "Your filters affect this widget — click to inspect"
                  "Filters applied — click to inspect"
                }}
                aria-expanded={{if this.showFilterDetails "true" "false"}}
                aria-haspopup="dialog"
                aria-controls={{t.panelId}}
                class="rounded-full px-1.5 py-0.5 tio-text-micro font-semibold
                  {{if
                    this.isViewerAdjusted
                    'tio-bg-brand-soft tio-fg-brand'
                    'tio-bg-info-soft tio-fg-info'
                  }}"
                {{t.register}}
                {{on "click" this.toggleFilterDetails}}
              >⏷ {{this.filterCount}}</button>
            </:trigger>
            <:body>
              <div data-test-tile-filter-details class="text-left">
                {{#if this.widgetFilterLines.length}}
                  <div
                    class="pb-1 tio-text-micro font-semibold uppercase tracking-wide tio-fg-muted"
                  >This widget</div>
                  {{#each this.widgetFilterLines as |line|}}
                    <div class="py-1 text-xs">
                      <span class="font-semibold">{{line.label}}</span>
                      <span class="tio-fg-muted">{{line.text}}</span>
                    </div>
                  {{/each}}
                {{/if}}
                {{#if this.dashboardFilterLines.length}}
                  <div
                    class="pb-1 pt-1 tio-text-micro font-semibold uppercase tracking-wide tio-fg-muted"
                  >Dashboard</div>
                  {{#each this.dashboardFilterLines as |line|}}
                    <div class="py-1 text-xs">
                      <span class="font-semibold">{{line.label}}</span>
                      <span class="tio-fg-muted">{{line.text}}</span>
                    </div>
                  {{/each}}
                {{/if}}
                {{#if this.hasUserFilters}}
                  <div
                    class="pb-1 pt-1 tio-text-micro font-semibold uppercase tracking-wide
                      {{if this.isViewerAdjusted 'tio-fg-brand' 'tio-fg-muted'}}"
                    data-test-tile-user-filter-heading
                  >{{this.userFilterHeading}}</div>
                  {{#each this.userFilterLines as |line|}}
                    <div class="py-1 text-xs">
                      <span class="font-semibold">{{line.label}}</span>
                      <span class="tio-fg-muted">{{line.text}}</span>
                    </div>
                  {{/each}}
                {{/if}}
                {{#if this.hasDrilldowns}}
                  <div
                    class="pb-1 pt-1 tio-text-micro tio-type-weight-semibold uppercase tracking-wide tio-fg-brand"
                    data-test-tile-drilldown-heading
                  >Drilled by</div>
                  {{#each this.drilldownLines as |line|}}
                    <div class="py-1 tio-type-caption" data-test-tile-drilldown-line>
                      <span class="tio-type-weight-semibold">{{line.label}}</span>
                      <span class="tio-fg-muted">{{line.text}}</span>
                    </div>
                  {{/each}}
                {{/if}}
              </div>
            </:body>
          </Popover>
        {{/if}}
        {{#if @onExpand}}
          <button
            type="button"
            data-test-tile-expand
            class="ml-auto shrink-0 tio-fg-muted hover:tio-fg-secondary"
            aria-label="Expand widget"
            title="Expand"
            {{on "click" this.handleExpand}}
          >⤢</button>
        {{/if}}
        {{#if @editable}}
          <span class="tio-row-extra-tight">
            <span class="select-none tio-fg-muted" aria-hidden="true">⋮⋮</span>
            <button
              type="button"
              data-test-tile-remove
              class="tio-fg-muted"
              aria-label="Remove widget"
              {{on "click" this.handleRemove}}
            >✕</button>
          </span>
        {{/if}}
      </header>
      <div class="min-h-0 flex-1 p-3">
        <WidgetRenderer
          @widget={{@widget}}
          @companyId={{@companyId}}
          @buildFilters={{@buildFilters}}
          @userFilters={{@userFilters}}
          @drilldowns={{@drilldowns}}
          @onDrilldown={{@onDrilldown}}
          @height={{this.chartHeight}}
        />
      </div>
    </div>
  </template>
}
